import { Component, OnInit, OnDestroy, signal, computed } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { RouterLink, ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { env } from '../config/env';
import { ApiService } from '../services/api.service';
import { Campaign } from '../models/types';
import { formatDuration } from '../utils/duration';
import { completionLabel } from '../utils/call-completion';

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './call-history.page.html',
  styleUrls: ['./call-history.page.css'],
})
export class CallHistoryPage implements OnInit, OnDestroy {
  search = '';
  campaignFilter = '';
  outcome = '';
  completion = '';
  direction = 'all';
  kind = 'campaign';
  dateFrom = '';
  dateTo = '';
  historyView = 'grouped';
  groupCounts = signal<{outcome:string,total:number,latest_started_at?:string}[]>([]);
  collapsedGroups = signal<Set<string>>(new Set());
  loadingGroups = signal<Set<string>>(new Set());
  contactGroups = computed(() => this.groupCounts().map(group => ({...group,
    contacts:this.contacts().filter(contact => (contact.disposition || 'unclassified') === group.outcome)
      .sort((a,b)=>Date.parse(b.started_at)-Date.parse(a.started_at))}))
    .sort((a,b)=>Date.parse(b.latest_started_at || b.contacts[0]?.started_at || '1970-01-01')-
      Date.parse(a.latest_started_at || a.contacts[0]?.started_at || '1970-01-01') || a.outcome.localeCompare(b.outcome)));
  private archiveRevision = 0;
  private appliedContactParams: Record<string,string> = {};
  offset = 0;
  readonly pageSize = 50;
  campaigns = signal<Campaign[]>([]);
  contacts = signal<any[]>([]);
  totalContacts = signal(0);
  selectedContact = signal<any>(null);
  attempts = signal<any[]>([]);
  totalAttempts = signal(0);
  selected = signal<any>(null);
  detail = signal<any>(null);
  loading = signal(false);
  attemptsLoading = signal(false);
  audioLoading = signal(false);
  audio = signal('');
  audioCallId = signal('');
  error = signal('');
  resetReview = signal(false);
  resetting = signal(false);
  resetMessage = signal('');
  outcomeEditor = signal<any>(null);
  outcomeBusy = signal(false);
  outcomeMessage = signal('');
  reviewOutcome = '';
  reviewReason = '';
  reviewRetry = false;
  reviewCallback = '';
  readonly outcomeOptions = ['completed','interested','converted','not_interested','callback','wrong_number','do_not_call',
    'transferred','no_answer','busy','no_response','voicemail','audio_problem','connection_failed','interrupted','incomplete'];
  readonly retryableOutcomes = ['no_answer','busy','no_response','voicemail','audio_problem','connection_failed','interrupted','incomplete'];
  private contactGeneration = 0;
  private generation = 0;
  private destroyed = false;
  formatDuration = formatDuration;
  completionLabel = completionLabel;
  Math = Math;

  constructor(private http: HttpClient, private api: ApiService, private route:ActivatedRoute) {}
  async ngOnInit() {
    const params=this.route.snapshot.queryParamMap;
    this.campaignFilter=params.get('campaign_id') || '';
    this.search=params.get('search') || '';
    if (['inbound','outbound'].includes(params.get('direction') || '')) this.direction=params.get('direction')!;
    const results = await Promise.allSettled([this.api.listCampaigns(), this.refresh()]);
    if (!this.destroyed && results[0].status === 'fulfilled') this.campaigns.set(results[0].value);
  }
  ngOnDestroy() {
    this.destroyed = true;
    clearTimeout(this.emailTimer);
    this.contactGeneration++;
    this.generation++;
    this.clearAudio();
  }
  outcomeLabel(value?: string) {
    const labels: Record<string,string> = {
      completed:'Enquiry / request handled', interested:'Interested', converted:'Confirmed sale',
      not_interested:'Not interested', wrong_number:'Wrong number', do_not_call:'Do not call',
      callback:'Callback requested', incomplete:'No clear conclusion', interrupted:'Conversation interrupted',
      audio_problem:'Audio problem', connection_failed:'Connection failed', no_response:'No response',
      no_answer:'No answer', busy:'Busy', voicemail:'Voicemail', transferred:'Transferred to team',
      unclassified:'Not classified',
    };
    return labels[value || ''] || (value || 'Not classified').replaceAll('_',' ');
  }
  attemptOutcome(call: any) {
    if (call.disposition) return this.outcomeLabel(call.disposition);
    if (call.completion_state === 'unverified') return 'Outcome unverified';
    if (call.completion_state === 'in_progress') return 'In progress';
    return this.outcomeLabel(call.status);
  }
  queueLabel(status: string) {
    return ({pending:'Queued for a new attempt',scheduled:'Follow-up queued',calling:'Call in progress',
      failed:'Team review needed',completed:'Concluded',do_not_call:'Suppressed'} as Record<string,string>)[status] || status;
  }
  initials(name?: string) {
    return (name || 'Unnamed contact').trim().split(/\s+/).slice(0,2).map(part => part[0]).join('').toUpperCase();
  }
  async refresh(resetPage = true) {
    if (this.loading() || this.destroyed) return;
    if (this.dateFrom && this.dateTo && this.dateFrom > this.dateTo) {
      this.error.set('From date must be on or before to date.'); return;
    }
    ++this.archiveRevision;
    if (resetPage) this.offset = 0;
    this.loading.set(true); this.error.set('');
    try {
      const params = {...this.contactParams(),grouped:String(this.historyView==='grouped'),offset:String(this.offset),limit:String(this.pageSize)};
      const result = await firstValueFrom(this.http.get<any>(`${env.apiUrl}/api/v1/call-history/contacts`, {params}));
      if (this.destroyed) return;
      this.contacts.set(result.contacts); this.totalContacts.set(result.total);
      this.groupCounts.set(result.groups || []);
      this.appliedContactParams = {...params};
      delete this.appliedContactParams['grouped'];
      const next = result.contacts.find((contact: any) => contact.contact_key === this.selectedContact()?.contact_key) || result.contacts[0];
      if (next) await this.selectContact(next);
      else this.clearSelection();
    } catch (error: any) { this.error.set(this.apiFailure(error,'Unable to load contact history.')); }
    finally { this.loading.set(false); }
  }
  private contactParams(): Record<string,string> {
    const params: Record<string,string> = {kind:this.kind,direction:this.direction};
    if (this.search.trim()) params['search'] = this.search.trim();
    if (this.campaignFilter) params['campaign_id'] = this.campaignFilter;
    if (this.outcome) params['outcome'] = this.outcome;
    if (this.completion) params['completion'] = this.completion;
    if (this.dateFrom) params['date_from'] = this.dateFrom;
    if (this.dateTo) params['date_to'] = this.dateTo;
    return params;
  }
  toggleGroup(outcome: string) {
    this.collapsedGroups.update(previous => {
      const next = new Set(previous); next.has(outcome) ? next.delete(outcome) : next.add(outcome); return next;
    });
  }
  setAllGroups(collapsed: boolean) {
    this.collapsedGroups.set(new Set(collapsed ? this.groupCounts().map(group => group.outcome) : []));
  }
  async loadGroup(group: {outcome:string,contacts:any[]}) {
    if (this.loading() || this.loadingGroups().has(group.outcome)) return;
    const revision = this.archiveRevision;
    this.loadingGroups.update(values => new Set([...values,group.outcome]));
    try {
      const params = {...this.appliedContactParams,outcome:group.outcome,offset:String(group.contacts.length),limit:'50'};
      const result = await firstValueFrom(this.http.get<any>(`${env.apiUrl}/api/v1/call-history/contacts`,{params}));
      if (this.destroyed || revision !== this.archiveRevision) return;
      this.contacts.update(rows => [...rows,...result.contacts.filter((row:any) => !rows.some(old => old.contact_key === row.contact_key))]);
    } catch { if (revision === this.archiveRevision) this.error.set('Unable to load more contacts in this outcome group.'); }
    finally { this.loadingGroups.update(values => { const next = new Set(values); next.delete(group.outcome); return next; }); }
  }
  clearDates() { this.dateFrom = ''; this.dateTo = ''; void this.refresh(); }
  async page(change: number) {
    if (this.loading()) return;
    this.offset = Math.max(0,this.offset + change * this.pageSize);
    await this.refresh(false);
  }
  private clearSelection() {
    this.contactGeneration++; this.generation++;
    this.selectedContact.set(null); this.selected.set(null); this.detail.set(null);
    this.attempts.set([]); this.totalAttempts.set(0); this.clearAudio();
  }
  async selectContact(contact: any) {
    const generation = ++this.contactGeneration;
    this.generation++;
    this.selectedContact.set(contact); this.selected.set(null); this.detail.set(null);
    this.resetReview.set(false); this.resetMessage.set(''); this.attempts.set([]);
    if (!this.outcomeBusy()) this.outcomeEditor.set(null);
    this.totalAttempts.set(contact.total_attempts); this.clearAudio();
    this.attemptsLoading.set(true); this.error.set('');
    try {
      const result = await this.fetchAttempts(contact,0);
      if (generation !== this.contactGeneration || this.destroyed) return;
      this.attempts.set(result.attempts); this.totalAttempts.set(result.total);
      if (result.attempts[0]) await this.selectAttempt(result.attempts[0]);
    } catch (error: any) { if (generation === this.contactGeneration) this.error.set(this.apiFailure(error,'Unable to load this contact’s attempts.')); }
    finally { if (generation === this.contactGeneration) this.attemptsLoading.set(false); }
  }
  private fetchAttempts(contact: any, offset: number) {
    return firstValueFrom(this.http.get<any>(`${env.apiUrl}/api/v1/call-history/contacts/${contact.kind}/${contact.id}/attempts`,
      {params:{offset:String(offset),limit:'50'}}));
  }
  async loadMoreAttempts() {
    const contact = this.selectedContact(), generation = this.contactGeneration;
    if (!contact || this.attemptsLoading()) return;
    this.attemptsLoading.set(true);
    try {
      const result = await this.fetchAttempts(contact,this.attempts().length);
      if (generation === this.contactGeneration && !this.destroyed) {
        this.attempts.update(attempts => [...attempts,...result.attempts]); this.totalAttempts.set(result.total);
      }
    } catch { this.error.set('Unable to load more attempts.'); }
    finally { if (generation === this.contactGeneration) this.attemptsLoading.set(false); }
  }
  private callUrl(call: any) { return `${env.apiUrl}/api/v1/call-history/${call.kind}/${call.id}`; }
  private apiFailure(error: any, fallback: string) {
    const detail = error?.error?.detail || error?.error?.message;
    if (typeof detail === 'string') return `${fallback} ${detail}`;
    if (error?.status === 401) return `${fallback} Your session has expired; sign in again.`;
    if (error?.status === 403) return `${fallback} Your account does not have call-history access.`;
    if (error?.status === 0) return `${fallback} The API could not be reached.`;
    if (error?.status) return `${fallback} API error ${error.status}. Check the backend logs.`;
    return `${fallback} ${error?.message || ''}`.trim();
  }
  async toggleFavorite(attempt: any, event?: Event) {
    event?.stopPropagation();
    if (attempt.favoriteBusy) return;
    const wasFavorite=!!attempt.is_favorite;
    attempt.favoriteBusy=true;
    try {
      const result=await firstValueFrom(this.http.post<any>(`${this.callUrl(attempt)}/favorite`,{is_favorite:!wasFavorite}));
      attempt.is_favorite=result.is_favorite;
      this.attempts.update(rows => rows.map(row => row.id===attempt.id ? {...row,is_favorite:result.is_favorite} : row));
      this.contacts.update(rows => rows.map(row => row.id===attempt.id && row.kind===attempt.kind ? {...row,is_favorite:result.is_favorite} : row));
    } catch (error:any) { this.error.set(this.apiFailure(error,'Could not update favorite.')); }
    finally {
      attempt.favoriteBusy=false;
      this.attempts.update(rows => rows.map(row => row.id===attempt.id ? {...row,favoriteBusy:false} : row));
      this.contacts.update(rows => rows.map(row => row.id===attempt.id && row.kind===attempt.kind ? {...row,favoriteBusy:false} : row));
    }
  }
  async selectAttempt(call: any) {
    if (!this.outcomeBusy()) this.outcomeEditor.set(null);
    const generation = ++this.generation;
    this.selected.set(call); this.detail.set(null); this.audioLoading.set(false); this.clearAudio();
    try {
      const result = await firstValueFrom(this.http.get<any>(this.callUrl(call)));
      if (generation === this.generation && !this.destroyed) this.detail.set(result);
    } catch { if (generation === this.generation) this.error.set('Unable to load call details.'); }
  }
  private clearAudio() {
    if (this.audio()) URL.revokeObjectURL(this.audio());
    this.audio.set(''); this.audioCallId.set('');
  }
  async listen(call: any) {
    if (this.selected()?.id !== call.id) await this.selectAttempt(call);
    if (this.selected()?.id !== call.id || this.audioLoading() || this.destroyed) return;
    const generation = this.generation;
    this.audioLoading.set(true);
    try {
      const blob = await firstValueFrom(this.http.get(this.callUrl(call)+'/recording',{responseType:'blob'}));
      if (generation === this.generation && !this.destroyed) {
        this.clearAudio(); this.audioCallId.set(call.id); this.audio.set(URL.createObjectURL(blob));
      }
    } catch { if (generation === this.generation) this.error.set('Unable to load this recording.'); }
    finally { if (generation === this.generation) this.audioLoading.set(false); }
  }
  async resetContact() {
    const contact = this.selectedContact();
    if (!contact?.lead_id || this.resetting() || this.outcomeBusy()) return;
    this.resetting.set(true);
    try {
      const result = await this.api.resetLeads(contact.campaign_id,'selected',[contact.lead_id]);
      if (this.destroyed || this.selectedContact()?.contact_key !== contact.contact_key) return;
      await this.refresh(false);
      if (this.destroyed || this.selectedContact()?.contact_key !== contact.contact_key) return;
      this.resetReview.set(false);
      this.resetMessage.set(`${result.reset_count} contact reset. ${result.skipped_active} active and ${result.skipped_suppressed} suppressed contacts skipped. Previous attempts and recordings remain available.`);
    } catch (error: any) {
      if (!this.destroyed && this.selectedContact()?.contact_key === contact.contact_key)
        this.resetMessage.set(error?.error?.detail || 'Could not reset this contact.');
    }
    finally { this.resetting.set(false); }
  }
  campaignActive(id: string) { return this.campaigns().some(campaign => campaign.id === id && campaign.status === 'active'); }
  async openOutcomeEditor(attempt: any) {
    if (this.outcomeBusy() || this.resetting()) return;
    const generation = this.contactGeneration;
    if (this.selected()?.id !== attempt.id) await this.selectAttempt(attempt);
    if (generation !== this.contactGeneration || this.destroyed) return;
    this.reviewOutcome = attempt.disposition || 'incomplete';
    this.reviewReason = ''; this.reviewRetry = false; this.reviewCallback = '';
    this.outcomeMessage.set(''); this.error.set('');
    this.outcomeEditor.set({...attempt,request_id:crypto.randomUUID()});
  }
  async saveOutcome() {
    const attempt = this.outcomeEditor(), contact = this.selectedContact();
    if (!attempt || this.outcomeBusy() || this.resetting() || !this.reviewReason.trim()) return;
    this.outcomeBusy.set(true); this.error.set('');
    try {
      const result = await firstValueFrom(this.http.patch<any>(this.callUrl(attempt)+'/outcome',{
        request_id:attempt.request_id,expected_outcome:attempt.disposition ?? null,
        outcome:this.reviewOutcome,reason:this.reviewReason.trim(),
        queue_retry:this.retryableOutcomes.includes(this.reviewOutcome) && this.reviewRetry,
        callback_at:this.reviewOutcome === 'callback' && this.reviewCallback ? this.reviewCallback+':00+05:00' : null,
      }));
      if (this.destroyed) return;
      this.outcomeEditor.set(null);
      await this.refresh(false);
      if (this.selectedContact()?.contact_key === contact?.contact_key) {
        const reviewed = this.attempts().find(row => row.id === attempt.id);
        if (reviewed) await this.selectAttempt(reviewed);
      }
      this.outcomeMessage.set(`Attempt #${attempt.attempt} · ${contact?.name || contact?.phone}: ${result.message} ${this.completionLabel(result.completion_state)}.`);
    } catch (error: any) { this.error.set(error?.error?.detail || 'Could not save the review. Retry keeps the same review reference.'); }
    finally { this.outcomeBusy.set(false); }
  }
  eventLabel(name: string) {
    return ({call_completed:'Call ended · summary',record_call_conclusion:'Conversation conclusion',
      create_business_enquiry:'Business enquiry · follow-up requested',record_contact_referral:'Contact referral · team review',manual_outcome_review:'Outcome corrected by operator'} as Record<string,string>)[name] || name.replaceAll('_',' ');
  }

  emailBusy = signal(false);
  emailMessage = signal('');
  emailResult = signal<any>(null);
  emailTestId = '';
  private emailTimer?: ReturnType<typeof setTimeout>;
  private emailPolls = 0;
  async sendEmailTest(reuseExisting = false) {
    if (this.emailBusy()) return;
    clearTimeout(this.emailTimer);
    if (!this.emailTestId || (!reuseExisting && this.emailResult())) this.emailTestId = crypto.randomUUID();
    this.emailBusy.set(true); this.emailMessage.set('Queuing test email…'); this.emailResult.set(null);
    try {
      const result = await firstValueFrom(this.http.post<any>(`${env.apiUrl}/api/v1/notification-tests/${this.emailTestId}`,{}));
      this.emailResult.set(result); this.emailMessage.set('Test saved. Checking delivery for up to one minute.');
      this.emailPolls = 0;
      if (!this.destroyed) this.emailTimer = setTimeout(() => void this.checkEmailTest(),3000);
    } catch (error: any) { this.emailMessage.set(error?.error?.detail || 'Could not confirm the request. Retry uses the same test reference.'); }
    finally { this.emailBusy.set(false); }
  }
  async checkEmailTest() {
    if (this.destroyed || !this.emailTestId || this.emailBusy()) return;
    clearTimeout(this.emailTimer); this.emailBusy.set(true);
    try {
      const result = await firstValueFrom(this.http.get<any>(`${env.apiUrl}/api/v1/notification-tests/${this.emailTestId}`));
      this.emailResult.set(result);
      if (!this.destroyed && ['queued','sending'].includes(result.status) && ++this.emailPolls < 20)
        this.emailTimer = setTimeout(() => void this.checkEmailTest(),3000);
      else this.emailMessage.set('Status updated. Checking again will not send another email.');
    } catch { this.emailMessage.set('Unable to check status. Use Check delivery status to retry.'); }
    finally { this.emailBusy.set(false); }
  }
}
